import { Box, Container, VStack } from '@chakra-ui/react'
import { revalidateLogic } from '@tanstack/react-form'
import { LanguageSwitch } from 'commons/components/layout/LanguageSwitch'
import { PageErrorCentered } from 'commons/components/layout/PageErrorCentered'
import PageLayout from 'commons/components/layout/PageLayout'
import { PageLoaderCentered } from 'commons/components/layout/PageLoaderCentered'
import { QrCodeButton } from 'commons/components/layout/QrCodeButton'
import { ShareButton } from 'commons/components/layout/ShareButton'
import { PaymentSubmit } from 'commons/components/PaymentSubmit'
import { UnifiedPaymentModal } from 'commons/components/modal/UnifiedPaymentModal'
import { FooterMoneyPot } from 'commons/components/moneyPot/FooterMoneyPot'
import { HeadingMoneyPot } from 'commons/components/moneyPot/HeadingMoneyPot'
import { IbanField } from 'commons/components/moneyPot/IbanField'
import QRCodeApp from 'commons/components/phone/QRCode'
import { useAppForm } from 'commons/config/form'
import { createSchema, defaultMoneyPotValues } from 'commons/config/value'
import { isXpayType, PaymentSources, PaymentTypes } from 'commons/constants/paymentTypes'
import { useFeatureFlipping } from 'commons/hooks/services/useFeatureFlipping'
import useMoneyPot from 'commons/hooks/services/useMoneyPot'
import { postMoneyPotRequest } from 'commons/services/api/paymentApi'
import type { PaymentSubmitRequest } from 'commons/types/paymentTypes'
import { toNumber } from 'commons/utils/amountHelpers'
import { extractSlugFromUrl } from 'commons/utils/slugHelpers'
import { isHideLydiaFromUrl } from 'commons/utils/hideLydiaHelpers'
import { getPendingPaymentUrl } from 'commons/utils/urlUtils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

/** Change these to tune emojis; key includes them so the scene remounts and picks up changes. */
const FLYING_EMOJI_SIZE = 1.1
const FLYING_EMOJI_COUNT = 10

const MoneyPotPage = () => {
	const [isUnifiedModalOpen, setIsUnifiedModalOpen] = useState(false)
	const [unifiedModalData, setUnifiedModalData] = useState<PaymentSubmitRequest | null>(null)
	const handlePaymentReset = useCallback(() => {
		setIsUnifiedModalOpen(false)
		setUnifiedModalData(null)
	}, [])
	const { data: featureFlippings } = useFeatureFlipping()
	const slug = useMemo(() => extractSlugFromUrl(), [])
	const { data: moneyPotData, isLoading, isError } = useMoneyPot(slug)
	const { t, i18n } = useTranslation()
	const hideLydia = useMemo(() => isHideLydiaFromUrl(), [])

	// SA-2682: Parse URL parameters for pre-filling form fields
	// ex: https://preprod.pay.lydia.me/l?t=ericg4810&a=2500&d=anniversaire&e=emile@mail.com
	const initialFormValues = useMemo(() => {
		const urlParams = new URLSearchParams(window.location.search)
		const values = { ...defaultMoneyPotValues }
		const amountParam = urlParams.get('a')
		if (amountParam) {
			const amountInCents = parseInt(amountParam, 10)
			if (!isNaN(amountInCents)) {
				values.amount = (amountInCents / 100).toString()
			}
		}
		const nameParam = urlParams.get('n')
		if (nameParam) {
			values.name = decodeURIComponent(nameParam)
		}
		const emailParam = urlParams.get('e')
		if (emailParam) {
			values.email = decodeURIComponent(emailParam)
		}
		if (hideLydia) {
			values.paymentType = PaymentTypes.CARD
		}
		return values
	}, [hideLydia])

	const form = useAppForm({
		defaultValues: initialFormValues,
		validationLogic: revalidateLogic({
			mode: 'blur',
			modeAfterSubmission: 'change'
		}),
		validators: {
			onDynamic: ({ formApi }) =>
				formApi.parseValuesWithSchema(
					createSchema(
						() => formApi.getFieldValue('amount'),
						() => formApi.getFieldValue('paymentType'),
						() => {
							const paymentType = formApi.getFieldValue('paymentType')
							return paymentType !== PaymentTypes.TRANSFER && paymentType !== PaymentTypes.LYDIA
						},
						false // requireMotif = false for MoneyPot (no MotifSection)
					)
				)
		},
		onSubmit: async ({ value, formApi }) => {
			// Discard the previous wallet session before creating a request with
			// the latest amount, payment method, and payer details.
			setIsUnifiedModalOpen(false)
			setUnifiedModalData(null)
			try {
				const amount = toNumber(value.amount) ?? 0
				await postMoneyPotRequest(
					amount,
					value.paymentType,
					value.phone || '',
					value.email || '',
					value.name || '',
					slug,
					(requestPubUuid: string, amount: number) => {
						setUnifiedModalData({
							requestPubUuid,
							amount,
							paymentType: value.paymentType,
							formValues: JSON.stringify(value)
						})
						setIsUnifiedModalOpen(true)
					}
				)
			} catch (error) {
				const errorMessage = error instanceof Error ? error.message : 'An error occurred'
				// Set error on the appropriate field based on payment type
				const errorObject = new Error(errorMessage)
				if (value.paymentType === PaymentTypes.LYDIA) {
					formApi.setFieldMeta('phone', (prev) => ({
						...prev,
						errors: [errorObject],
						isTouched: true,
						isValid: false,
						errorMap: { onSubmit: errorMessage }
					}))
				} else {
					formApi.setFieldMeta('email', (prev) => ({
						...prev,
						errors: [errorObject],
						isTouched: true,
						isValid: false,
						errorMap: { onSubmit: errorMessage }
					}))
				}
				// Rethrow to prevent form from thinking submission succeeded
				throw error
			}
		},
		onSubmitInvalid: ({ formApi }) => {
			// Force touch all fields to show errors
			const fieldNames = ['amount', 'paymentType', 'name', 'phone', 'email'] as const
			fieldNames.forEach((fieldName) => {
				formApi.setFieldMeta(fieldName, (prev) => ({ ...prev, isTouched: true }))
			})
		}
	})

	// Track previous payment type to clear fields only when it changes
	const prevPaymentTypeRef = useRef(defaultMoneyPotValues.paymentType)

	useEffect(() => {
		const unsubscribe = form.store.subscribe(() => {
			const currentPaymentType = form.getFieldValue('paymentType')
			const prevPaymentType = prevPaymentTypeRef.current
			if (currentPaymentType !== prevPaymentType) {
				if (currentPaymentType === PaymentTypes.LYDIA) {
					// Clear email and name when switching to Lydia
					const emailValue = form.getFieldValue('email')
					if (emailValue) {
						form.setFieldValue('email', '')
					}
					const nameValue = form.getFieldValue('name')
					if (nameValue) {
						form.setFieldValue('name', '')
					}
				} else if (currentPaymentType === PaymentTypes.CARD || isXpayType(currentPaymentType)) {
					// Clear phone when switching to Card
					const phoneValue = form.getFieldValue('phone')
					if (phoneValue) {
						form.setFieldValue('phone', '')
					}
				}
				prevPaymentTypeRef.current = currentPaymentType
			}
		})
		return unsubscribe
	}, [form])

	const handlePaymentCompleted = useCallback(() => {
		if (!unifiedModalData) return
		window.location.href = getPendingPaymentUrl(
			process.env.REACT_APP_POTS_URL,
			unifiedModalData.requestPubUuid,
			i18n.language
		)
	}, [unifiedModalData, i18n])

	if (isLoading) {
		return <PageLoaderCentered />
	}

	if (isError) {
		return <PageErrorCentered message={t('money_pot.error_fetching_data')} />
	}

	return (
		<PageLayout>
			<Box position='absolute' top={'25px'} right={{ base: '26px', md: '60px' }} zIndex={1}>
				<ShareButton isFromMoneyPot />
				<QrCodeButton slug={slug} title={moneyPotData?.title} />
			</Box>
			<Container maxW='2xl' centerContent w='auto' minW={{ md: '600px' }} p={4}>
				{moneyPotData && (
					<HeadingMoneyPot
						key={`heading-${FLYING_EMOJI_SIZE}-${FLYING_EMOJI_COUNT}`}
						title={moneyPotData.title}
						image_src={moneyPotData.image_src}
						collector_name={moneyPotData.collector_name}
						show_balance={moneyPotData.show_balance}
						show_total={moneyPotData.show_total}
						balance={moneyPotData.balance}
						total_collected={moneyPotData.total_collected}
						show_description={moneyPotData.show_description}
						description={moneyPotData.description}
						emoji={moneyPotData.emoji}
						emojiCount={FLYING_EMOJI_COUNT}
						emojiSize={FLYING_EMOJI_SIZE}
					/>
				)}
				<VStack
					gap={0.5}
					w='full'
					mt={10}
					as='form'
					maxW='600px'
					position='relative'
					zIndex={2}
					onSubmit={(e: React.FormEvent<HTMLDivElement>) => {
						e.preventDefault()
						e.stopPropagation()
					}}
				>
					<form.AppForm>
						{form.AppField({
							name: 'amount',
							children: (field) => <field.AmountSection isFromMoneyPot />
						})}
						{form.AppField({
							name: 'paymentType',
							children: (field) => (
								<field.PaymentTypeSection
									featureFlippings={featureFlippings}
									isFromMoneyPot
									collectIban={moneyPotData?.collectIban}
									hideLydia={hideLydia}
								/>
							)
						})}
						{form.Subscribe({
							selector: (state) => ({
								paymentType: state.values.paymentType,
								amount: state.values.amount
							}),
							children: ({ paymentType, amount }) => (
								<>
									{paymentType === PaymentTypes.LYDIA && (
										<Box display={{ base: 'none', md: 'block' }} w='full'>
											<QRCodeApp
												context='money_pot'
												identifier={slug}
												amount={amount}
												lydiaLogoUrl={`${process.env.REACT_APP_POTS_URL}collect/assets/img/logo_app_lydia.png`}
												sumeriaLogoUrl={`${process.env.REACT_APP_POTS_URL}collect/assets/img/icons/lydiaSumeriaSmall.png`}
											/>
										</Box>
									)}
									{paymentType !== PaymentTypes.TRANSFER &&
										paymentType !== PaymentTypes.LYDIA &&
										form.AppField({
											name: 'name',
											children: (field) => <field.NameSection />
										})}
									{paymentType === PaymentTypes.LYDIA &&
										form.AppField({
											name: 'phone',
											children: (field) => <field.PhoneSection />
										})}
									{(paymentType === PaymentTypes.CARD || isXpayType(paymentType)) &&
										form.AppField({
											name: 'email',
											children: (field) => <field.EmailSection />
										})}
									{paymentType === PaymentTypes.TRANSFER && moneyPotData?.collectIban && (
										<IbanField
											iban={moneyPotData.collectIban}
											name={moneyPotData.collector_name}
											bic={moneyPotData.collectBic}
											identifier={slug}
											amount={toNumber(amount) ?? 0}
											source={PaymentSources.MONEY_POT}
										/>
									)}
								</>
							)
						})}
						{form.Subscribe({
							selector: (state) => state.values.paymentType,
							children: (paymentType) =>
								(unifiedModalData && isXpayType(unifiedModalData.paymentType)) ||
								paymentType !== PaymentTypes.TRANSFER ? (
									<PaymentSubmit
										request={isUnifiedModalOpen ? unifiedModalData : null}
										onReset={handlePaymentReset}
										onPaymentCompleted={handlePaymentCompleted}
										isFromMoneyPot
									/>
								) : null
						})}
					</form.AppForm>
					<FooterMoneyPot firstname={moneyPotData?.collector_name} />
					<LanguageSwitch />
				</VStack>
			</Container>
			{unifiedModalData && !isXpayType(unifiedModalData.paymentType) && (
				<UnifiedPaymentModal
					key={unifiedModalData.requestPubUuid}
					isOpen={isUnifiedModalOpen}
					onClose={handlePaymentReset}
					requestPubUuid={unifiedModalData.requestPubUuid}
					onPaymentCompleted={handlePaymentCompleted}
					source='money_pot'
					paymentType={unifiedModalData.paymentType}
					amount={unifiedModalData.amount}
				/>
			)}
		</PageLayout>
	)
}

export default MoneyPotPage
